// eslint-disable-next-line camelcase
import { encode_object } from 'rison'
import i18next from 'i18next'
import { getPanelTop } from 'weblite-router'
import {
  ACHIEVE_WAKE_LOCK,
  AchieveWakeLockAPI,
  BlockedPermissionType,
  Exceptions,
  GET_APP_CONFIG,
  GetConfigAPI,
  OPEN_IDENTIFIER,
  OPEN_IMAGE,
  OPEN_LINK,
  OPEN_LOCATION_PICKER,
  OPEN_VIDEO,
  OpenIdentifierAPI,
  OpenImageAPI,
  OpenLinkAPI,
  OpenLocationPickerAPI,
  OpenVideoAPI,
  RELEASE_WAKE_LOCK,
  ReleaseWakeLockAPI,
  SET_CUSTOM_ACTIONS,
  SET_NATIVE_COLORS,
  SetCustomActionsAPI,
  SetNativeColorsAPI,
  SHARE,
  ShareAPI,
  ShareEvent,
  SHOW_PERMISSION_BLOCKED_DIALOG,
  ShowPermissionBlockedDialogAPI,
  WappMode,
  TOGGLE_FULLSCREEN,
  ToggleFullscreenAPI,
  eventExitedFullscreen,
  OpenSupportApi,
  OPEN_SUPPORT,
} from '@weblite-wapps/protocol'
import { setWappCustomActions } from 'weblite-web-wapprunner'
import { homePanelAPI } from '$scenes/home'
import { wappPanelAPI } from '$comp/wapp-panel'
import { showSelectCounty } from '$comp/modals/select-menu'
import { showShareWappModal } from '$comp/modals/share-wapp-modal'
import { showVideoPlayerModal, VideoDetails } from '$comp/modals/video-player'
import { ImageDetails, showImageViewerModal } from '$comp/modals/image-viewer'
import { showLinkSavedInMemorySnackbar } from '$comp/snackbars'
import {
  showMicPermissionDeniedDialog,
  showCameraPermissionDeniedDialog,
  showContactsPermissionDeniedDialog,
  showNotificationsPermissionDeniedDialog,
} from '$comp/modals/permission-dialog'
import { wappSubscription } from '$logic/wapp-communication/helper/subscription-handler'
import { handleLink } from '$logic/router/url'
import { shareInlineWapp } from '$logic/wapp-communication/helper/utils.helper'
import { disableNoSleep, enableNoSleep } from '$func/no-sleep'
import { onIdentifierClick } from '$func/identifier'
import { WAPP_EMOJI_SPRITE_SHEET } from '$helper/constants/emoji'
import { copyToClipboard } from '$func/clipboard'
import { getWappInput } from '$logic/wapp-communication/helper/wapps.helper'
import { getStaticPanelByWappSessionId } from '$logic/wapp-panels'
import { canShareLink, shareLink } from '$func/share-api'
import { createShortLink } from '$func/link-shortener'
import { addProtocolToUrl } from '$func/link'
import { Palette, theme } from '$setup/material-theme'
import hostConfig from '$config/frontend'
import { getOffset } from '$logic/client-device/time-offset.service'
import {
  enterElementToFullscreenMode,
  exitElementFromFullscreenMode,
  FullscreenLocation,
} from '$logic/fullscreen/fullscreen-mode'
import { openSupport } from '$func/open-support'
import { sendEventToWapp } from '$logic/wapp-communication/wapp-communication.publish'

wappSubscription.subscribe<OpenLinkAPI>(OPEN_LINK, async ({ link }) => {
  await handleLink(link)
  return { status: 'ok' }
})

wappSubscription.subscribe<OpenImageAPI>(OPEN_IMAGE, async payload => {
  if (getPanelTop('modals')?.location)
    return { errorCode: Exceptions.TEMPORARILY_NOT_AVAILABLE }

  const { height, width, thumbnail, ...other } = payload.images[0]
  const image = {
    ...other,
    attachment: { base64: thumbnail, height, width },
  } as ImageDetails

  await showImageViewerModal({ media: image })
  return { status: 'ok' }
})

wappSubscription.subscribe<OpenIdentifierAPI>(
  OPEN_IDENTIFIER,
  async payload => {
    await onIdentifierClick(payload.identifier)
    return { status: 'ok' }
  },
)

wappSubscription.subscribe<OpenVideoAPI>(OPEN_VIDEO, async payload => {
  if (getPanelTop('modals')?.location)
    return { errorCode: Exceptions.TEMPORARILY_NOT_AVAILABLE }

  const { durationInSeconds, height, width, thumbnail, ...other } =
    payload.videos[0]

  // For enabling video player to capture events we should put focus back from wapp iframe to our window.
  window.focus()

  const video = {
    ...other,
    attachment: {
      base64: thumbnail,
      height,
      width,
      duration: durationInSeconds,
    },
  } as VideoDetails

  await showVideoPlayerModal({ media: video })
  return { status: 'ok' }
})

wappSubscription.subscribe<ShareAPI>(SHARE, async (payload, meta) => {
  const { contentUri, mainModeInfo, inlineModeInfo, shareText } = payload
  if (!mainModeInfo && !inlineModeInfo) return { errorCode: Exceptions.INVALID }

  if (mainModeInfo) {
    return new Promise<{ events: ShareEvent[] }>(res => {
      const events: ShareEvent[] = []

      async function getCurrentWappDirectLink() {
        const { wappId, wisId, mode, wappSessionId } = meta

        const url = new URL(`${hostConfig.FRONT_HOST}/wapp/${wappId}`)

        if (mode === 'overlay') url.searchParams.append('mode', mode)
        if (wisId) url.searchParams.append('wisId', wisId)

        const input = await getWappInput(wappSessionId, wisId)
        const encodedInput = encode_object(input)
        if (encodedInput) url.searchParams.append('input', encodedInput)

        return url.toString()
      }

      async function onForward() {
        await shareInlineWapp(inlineModeInfo, true, contentUri)
        events.push(ShareEvent.INTERNAL_SHARE)
        res({ events })
      }
      async function onCopyLink() {
        const link = await getCurrentWappDirectLink()
        const shortLink = await createShortLink(link)
        await copyToClipboard(shortLink)
        showLinkSavedInMemorySnackbar()
        events.push(ShareEvent.COPY_LINK)
      }
      async function onShare() {
        const link = await getCurrentWappDirectLink()
        const shortLink = await createShortLink(link)
        shareLink(addProtocolToUrl(shortLink), shareText).onShare?.()
        events.push(ShareEvent.EXTERNAL_SHARE)
      }

      const canForward = Boolean(inlineModeInfo)
      if (canForward) onForward()
      else
        showShareWappModal({
          canForward: Boolean(inlineModeInfo),
          onForward,
          onCopyLink,
          onShare: canShareLink() ? onShare : null,
        })
    })
  }
  if (inlineModeInfo) {
    await shareInlineWapp(inlineModeInfo, false, contentUri)
    return { events: [ShareEvent.INTERNAL_SHARE] }
  }

  return { errorCode: Exceptions.TEMPORARILY_NOT_AVAILABLE }
})

wappSubscription.subscribe<SetCustomActionsAPI>(
  SET_CUSTOM_ACTIONS,
  async ({ actionNames }, meta) => {
    if (actionNames?.length)
      setWappCustomActions(meta.wappSessionId, actionNames)

    return { status: 'ok' }
  },
)

wappSubscription.subscribe<OpenLocationPickerAPI>(OPEN_LOCATION_PICKER, () => {
  return new Promise(showSelectCounty)
})

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
wappSubscription.subscribe<GetConfigAPI>(GET_APP_CONFIG, () => {
  return {
    language: i18next.language,
    vod: hostConfig.VOD,
    storage: {
      fileServiceUrl: hostConfig.FILE_HOST,
      s3Url: hostConfig.S3_HOST,
      imageResizeApiAddress: hostConfig.IMAGE_COMPRESSION_API_ADDRESS,
    },
    wappServerTemplate: hostConfig.WAPP_SERVER_TEMPLATE,
    theme: {
      direction: theme.direction,
      palette: theme.palette as any,
    },
    emojiSpriteSheet: WAPP_EMOJI_SPRITE_SHEET,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    sentry: { host: import.meta.env.VITE_WAPP_SENTRY_HOST },
    date: {
      offset: getOffset(),
    },
  }
})

wappSubscription.subscribe<AchieveWakeLockAPI>(
  ACHIEVE_WAKE_LOCK,
  async (payload, meta) => {
    enableNoSleep(`${meta.wappSessionId}${payload}`)
    return { status: 'ok' }
  },
)

wappSubscription.subscribe<ReleaseWakeLockAPI>(
  RELEASE_WAKE_LOCK,
  async (payload, meta) => {
    disableNoSleep(`${meta.wappSessionId}${payload}`)
    return { status: 'ok' }
  },
)

wappSubscription.subscribe<SetNativeColorsAPI>(
  SET_NATIVE_COLORS,
  async ({ statusBarColor, bottomSafeAreaColor }, meta) => {
    if (meta.mode !== WappMode.STATIC && meta.mode !== WappMode.MAIN)
      return { errorCode: Exceptions.NOT_AVAILABLE }

    const statusBarFinalColor =
      statusBarColor === 'default' ? undefined : statusBarColor
    const bottomSafeAreaFinalColor =
      bottomSafeAreaColor === 'default' ? undefined : bottomSafeAreaColor

    if (meta.mode === WappMode.STATIC) {
      const panelName = getStaticPanelByWappSessionId(
        meta.wappSessionId,
      ).getName()

      if (['mavakeb', 'jaryan', 'kooleh'].includes(panelName))
        homePanelAPI.setNativeColors({
          statusBar: Palette.neutral000,
          bottomSafeArea: bottomSafeAreaFinalColor,
        })
    } else if (meta.mode === WappMode.MAIN) {
      wappPanelAPI.setNativeColors({
        statusBar: statusBarFinalColor,
        bottomSafeArea: bottomSafeAreaFinalColor,
      })
    }

    return { status: 'ok' }
  },
)

wappSubscription.subscribe<ShowPermissionBlockedDialogAPI>(
  SHOW_PERMISSION_BLOCKED_DIALOG,
  async ({ type: permissionType }, meta) => {
    if (permissionType === BlockedPermissionType.CAMERA)
      showCameraPermissionDeniedDialog()
    else if (permissionType === BlockedPermissionType.MIC)
      showMicPermissionDeniedDialog()
    else if (permissionType === BlockedPermissionType.NOTIFICATION)
      showNotificationsPermissionDeniedDialog()
    else if (permissionType === BlockedPermissionType.CONTACTS)
      showContactsPermissionDeniedDialog()
    else return { errorCode: Exceptions.INVALID }

    return { status: 'ok' }
  },
)

const wappModeToFullscreenLocationMap: Record<WappMode, FullscreenLocation> = {
  [WappMode.INLINE]: FullscreenLocation.ROOM,
  // If you want to enter wapps other than inline wapps to fullscreen, you should add another fullscreen location
  // and use useHandleFullscreenHistory hook in the proper location to handle fullscreen history
  [WappMode.STATIC]: FullscreenLocation.NONE,
  [WappMode.MAIN]: FullscreenLocation.NONE,
  [WappMode.OVERLAY]: FullscreenLocation.NONE,
}
wappSubscription.subscribe<ToggleFullscreenAPI>(
  TOGGLE_FULLSCREEN,
  async ({ enable }, meta) => {
    const wappSessionId = meta.wappSessionId
    const wappIframeId = `#${wappSessionId}`

    enable
      ? enterElementToFullscreenMode(
          wappIframeId,
          wappModeToFullscreenLocationMap[meta.mode],
          () => {
            sendEventToWapp(eventExitedFullscreen(null, meta))
          },
        )
      : exitElementFromFullscreenMode(wappIframeId)

    return { status: 'ok' }
  },
)

wappSubscription.subscribe<OpenSupportApi>(OPEN_SUPPORT, async () => {
  await openSupport()
  return { status: 'ok' }
})
